import { lazy, Suspense, useState } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Navigate } from 'react-router-dom'
import { NavSidebar } from '@/components/NavSidebar'
import { MwNavBar } from '@/components/mw/MwNavBar'
import { MwSubscriptionGate } from '@/components/mw'
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus'
import { Skeleton, SkeletonParagraph } from '@/components/Skeleton'
import { ShortcutsModal } from '@/components/ShortcutsModal'
import { FocusTimer } from '@/components/FocusTimer'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { useKeyboardShortcuts, type Shortcut } from '@/hooks/useKeyboardShortcuts'
import {
  defaultCourseSlug,
  getCourse,
  contentModules as registryContentModules,
} from '@/lib/coursesRegistry'
import { CourseProvider, useCourse } from '@/contexts/CourseContext'
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { ToastProvider } from '@/contexts/ToastContext'
import { TenantProvider } from '@/contexts/TenantContext'
import { resolveTenantSlug } from '@/lib/tenant'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { getFocusSnapshot, startWork, pause, resume } from '@/lib/focusStore'
import { usePlatformSettings } from '@/hooks/usePlatformSettings'
import { getCoursePricing } from '@/lib/coursePricing'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { fetchMyRole } from '@/lib/organizationsDb'

// SearchPalette arrastra todo el contenido del curso (índice). Lazy-load
// para que no entre en el bundle inicial; el chunk se descarga la primera
// vez que el alumno abre la búsqueda con Cmd+K, / o el botón del header.
const SearchPalette = lazy(() =>
  import('@/components/SearchPalette').then(m => ({ default: m.SearchPalette })),
)

// Las páginas se cargan bajo demanda con React.lazy. Esto baja el
// bundle inicial; Vite genera un chunk por página.
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })))
const VerifyCertificatePage = lazy(() =>
  import('@/pages/VerifyCertificatePage').then(m => ({ default: m.VerifyCertificatePage })),
)
const TeamPanelPage = lazy(() =>
  import('@/pages/TeamPanelPage').then(m => ({ default: m.TeamPanelPage })),
)
const NotesPage = lazy(() =>
  import('@/pages/NotesPage').then(m => ({ default: m.NotesPage })),
)
const LessonPage = lazy(() =>
  import("@/pages/LessonPage").then(m => ({ default: m.LessonPage })),
)
const ProgressPage = lazy(() => import('@/pages/ProgressPage').then(m => ({ default: m.ProgressPage })))
const CertificatePage = lazy(() => import('@/pages/CertificatePage').then(m => ({ default: m.CertificatePage })))
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then(m => ({ default: m.ProfilePage })))
const PrivacySettingsPage = lazy(() => import('@/pages/PrivacySettingsPage').then(m => ({ default: m.PrivacySettingsPage })))
const SubscriptionPage = lazy(() => import('@/pages/SubscriptionPage').then(m => ({ default: m.SubscriptionPage })))
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage'))
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage').then(m => ({ default: m.CheckoutPage })))
const PaywallPage = lazy(() => import('@/pages/PaywallPage').then(m => ({ default: m.PaywallPage })))
const SessionOneToOnePage = lazy(() => import('@/pages/SessionOneToOnePage').then(m => ({ default: m.SessionOneToOnePage })))
const CheckoutSuccessPage = lazy(() => import('@/pages/CheckoutResultPages').then(m => ({ default: m.CheckoutSuccessPage })))
const CheckoutCancelledPage = lazy(() => import('@/pages/CheckoutResultPages').then(m => ({ default: m.CheckoutCancelledPage })))
const ProductCatalogPage = lazy(() => import('@/pages/ProductCatalogPage').then(m => ({ default: m.ProductCatalogPage })))
const GiftPurchasePage = lazy(() => import('@/pages/GiftPurchasePage').then(m => ({ default: m.GiftPurchasePage })))
const GiftSuccessPage = lazy(() => import('@/pages/GiftSuccessPage').then(m => ({ default: m.GiftSuccessPage })))
const GiftRedeemPage = lazy(() => import('@/pages/GiftRedeemPage').then(m => ({ default: m.GiftRedeemPage })))
const BundlePurchasePage = lazy(() => import('@/pages/BundlePurchasePage').then(m => ({ default: m.BundlePurchasePage })))
const GiftBundlePurchasePage = lazy(() => import('@/pages/GiftBundlePurchasePage').then(m => ({ default: m.GiftBundlePurchasePage })))
const BundleSuccessPage = lazy(() => import('@/pages/BundleSuccessPage').then(m => ({ default: m.BundleSuccessPage })))
const CalculatorsCatalogPage = lazy(() => import('@/pages/CalculatorsCatalogPage').then(m => ({ default: m.CalculatorsCatalogPage })))
const CalculatorPage = lazy(() => import('@/pages/CalculatorPage').then(m => ({ default: m.CalculatorPage })))
const CoursePublicLandingPage = lazy(() =>
  import('@/pages/CoursePublicLandingPage').then(m => ({ default: m.CoursePublicLandingPage })),
)
const MentorWalletLandingPage = lazy(() => import('@/pages/MentorWalletLandingPage'))
const B2BLandingPage = lazy(() => import('@/pages/B2BLandingPage').then(m => ({ default: m.B2BLandingPage })))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const CookieBanner = lazy(() =>
  import('@/components/CookieBanner').then(m => ({ default: m.CookieBanner })),
)
const RepasoPage = lazy(() => import('@/pages/RepasoPage').then(m => ({ default: m.RepasoPage })))
const LoginPage = lazy(() => import('@/pages/LoginPage').then(m => ({ default: m.LoginPage })))
const CatalogPage = lazy(() => import('@/pages/CatalogPage').then(m => ({ default: m.CatalogPage })))
const MwDashboardPage = lazy(() => import("@/pages/MwDashboardPage").then(m => ({ default: m.MwDashboardPage })))
const MwNotFoundPage = lazy(() => import("@/pages/MwNotFoundPage").then(m => ({ default: m.MwNotFoundPage })))
const VerifyPage = lazy(() => import('@/pages/VerifyPage').then(m => ({ default: m.VerifyPage })))
const DiagnosticPage = lazy(() => import('@/pages/DiagnosticPage').then(m => ({ default: m.DiagnosticPage })))
const DiagnosticResultPage = lazy(() => import('@/pages/DiagnosticResultPage').then(m => ({ default: m.DiagnosticResultPage })))
const MyPathPage = lazy(() => import('@/pages/MyPathPage').then(m => ({ default: m.MyPathPage })))
const MwOnboardingPage = lazy(() => import('@/pages/MwOnboardingPage'))

// Panel admin MentorWallet (Fase R.2). Lazy-load: no entra al bundle del
// alumno normal.
const AdminLayout = lazy(() =>
  import('@/components/AdminLayout').then(m => ({ default: m.AdminLayout })),
)
const AdminDashboardPage = lazy(() =>
  import('@/pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })),
)
const AdminUsersListPage = lazy(() =>
  import('@/pages/admin/AdminUsersListPage').then(m => ({
    default: m.AdminUsersListPage,
  })),
)
const AdminPurchasesListPage = lazy(() =>
  import('@/pages/admin/AdminPurchasesListPage').then(m => ({
    default: m.AdminPurchasesListPage,
  })),
)
const AdminAnalyticsPage = lazy(() =>
  import('@/pages/admin/AdminAnalyticsPage').then(m => ({
    default: m.AdminAnalyticsPage,
  })),
)
const AdminRoutesPage = lazy(() =>
  import('@/pages/admin/AdminRoutesPage').then(m => ({ default: m.AdminRoutesPage })),
)
const AdminStudentPreviewPage = lazy(() =>
  import('@/pages/admin/AdminStudentPreviewPage').then(m => ({
    default: m.AdminStudentPreviewPage,
  })),
)
const AdminOrganizationsPage = lazy(() =>
  import('@/pages/admin/AdminOrganizationsPage').then(m => ({ default: m.AdminOrganizationsPage })),
)
const AdminCoursesPage = lazy(() =>
  import("@/pages/admin/AdminCoursesPage").then(m => ({
    default: m.AdminCoursesPage,
  })),
)
const AdminCourseEditorPage = lazy(() =>
  import("@/pages/admin/AdminCourseEditorPage").then(m => ({
    default: m.AdminCourseEditorPage,
  })),
)
const AdminBlockEditorPage = lazy(() =>
  import("@/pages/admin/AdminBlockEditorPage").then(m => ({
    default: m.AdminBlockEditorPage,
  })),
)

// Panel admin de organización (Fase R.3).

// Basename del router:
//   - Modo offline (`VITE_OFFLINE=1` durante build): vacío para que las
//     rutas SPA funcionen al servir desde cualquier carpeta.
//   - Producción (Vercel sirviendo mentor-wallet.plainvanilla.ai en raíz): vacío.
//   - Dev local: vacío.
//
// El antiguo deploy a GitHub Pages requería `/agent365-cert-course` como
// basename porque vivía en `plainvanillaesp.github.io/agent365-cert-course/`.
// Tras la migración a Vercel + dominio propio (fase Q), el sitio vive en
// raíz, así que cualquier basename hace que el router no encuentre la URL
// actual y se quede en pantalla blanca.
const basename = ''

/**
 * Fallback de Suspense mientras carga una página. Mantiene el shell
 * (header + sidebar) ya pintado por App; aquí solo el contenido
 * central enseña skeleton.
 */
function PageFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Cargando"
      className="mx-auto py-8"
      style={{ maxWidth: "var(--mw-w-content)" }}
    >
      <Skeleton width="40%" height="28px" shape="rect" />
      <div className="mt-6">
        <SkeletonParagraph lines={3} />
      </div>
      <div className="mt-8">
        <Skeleton width="100%" height="160px" shape="rect" />
      </div>
      <div className="mt-6">
        <SkeletonParagraph lines={2} />
      </div>
    </div>
  )
}

/**
 * Componente interno que sí está dentro del Router para poder usar
 * los hooks de react-router. Recibe el toggle del sidebar y el setter
 * del modal de atajos.
 */
function AppShell({
  navOpen,
  setNavOpen,
  shortcutsOpen,
  setShortcutsOpen,
  searchOpen,
  setSearchOpen,
}: {
  navOpen: boolean
  setNavOpen: (v: boolean) => void
  shortcutsOpen: boolean
  setShortcutsOpen: (v: boolean) => void
  searchOpen: boolean
  setSearchOpen: (v: boolean) => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ id?: string; section?: string }>()

  // ─── TENANT-AWARE CHROME ──────────────────────────────────────
  // MentorWallet tiene su propio header y diseño en cada página.
  // El shell global de PV-Learn (Header con logo PV, theme toggle,
  // lang switch) no debe aparecer cuando el tenant es mentor-wallet:
  // duplica el branding y rompe el look MW.
  //
  // Usamos el TenantContext si está disponible; en su defecto,
  // caemos al subdomain detection (mismo método que usa HomeSwitcher).
  // Algunas rutas tempranas (e.g. /login redirect) pueden montar el
  // AppShell antes de que el TenantProvider tenga datos cargados,
  // por eso el doble check.

  // Slug y módulo activos derivados de la URL. La ruta canónica es
  // `/cursos/<slug>/modulo/<id>/<section>`. Si la URL no encaja, los
  // atajos relacionados con módulos no hacen nada (los globales sí).
  const urlMatch = location.pathname.match(/^\/cursos\/([^/]+)(?:\/modulo\/(\d+))?/)
  const activeSlug = urlMatch?.[1] ?? defaultCourseSlug()
  const currentModuleId = urlMatch?.[2] ? parseInt(urlMatch[2], 10) : null
  // `activeSlug` se usa para coursePath (shortcuts del teclado). El
  // CourseProvider real se monta más abajo (MaybeCourseProvider)
  // solo si la URL es ruta de curso.

  const coursePath = (path = '') => {
    const clean = path.replace(/^\/+/, '')
    return `/cursos/${activeSlug}${clean ? '/' + clean : ''}`
  }

  const goToModuleDelta = (delta: number) => {
    if (currentModuleId === null) return
    // Los atajos del teclado navegan entre módulos del curso ACTIVO,
    // no del 'curso por defecto' global. Cuando hay varios cursos
    // en el registry, esto evita que alt+j te lleve a un módulo de
    // otro curso.
    const activeCourse = getCourse(activeSlug)
    if (!activeCourse) return
    const ids = registryContentModules(activeCourse)
      .filter(m => m.estado === 'producido')
      .map(m => m.id)
    const idx = ids.indexOf(currentModuleId)
    if (idx < 0) return
    const next = ids[idx + delta]
    if (next === undefined) return
    navigate(coursePath(`leccion/${next}`))
  }

  // Definición de atajos globales. Algunos solo tienen efecto en
  // determinadas rutas; en otras simplemente no hacen nada (no muestran
  // error). El modal de ayuda los lista todos.
  const shortcuts: Shortcut[] = [
    {
      key: '?',
      shift: true, // '?' requiere shift en el layout US
      description: 'Mostrar esta ayuda',
      group: 'Ayuda',
      handler: () => setShortcutsOpen(true),
    },
    {
      key: 'k',
      meta: true,
      description: 'Buscar en el curso',
      group: 'Ayuda',
      enableInInputs: true,
      handler: () => setSearchOpen(true),
    },
    {
      key: '/',
      description: 'Buscar en el curso',
      group: 'Ayuda',
      handler: () => setSearchOpen(true),
    },
    {
      key: 'g',
      description: 'Ir al inicio',
      group: 'Navegación',
      handler: () => navigate(coursePath()),
    },
    {
      key: 'p',
      description: 'Ir al progreso',
      group: 'Navegación',
      handler: () => navigate(coursePath('progreso')),
    },
    {
      key: 'e',
      description: 'Ir al examen',
      group: 'Navegación',
      handler: () => navigate(coursePath('examen')),
    },
    {
      key: 's',
      description: 'Ir a ajustes',
      group: 'Navegación',
      handler: () => navigate('/ajustes'),
    },
    {
      key: 'R',
      shift: true,
      description: 'Ir al repaso (flashcards)',
      group: 'Navegación',
      handler: () => navigate(coursePath('repaso')),
    },
    {
      key: 'j',
      description: 'Siguiente módulo',
      group: 'En un módulo',
      handler: () => goToModuleDelta(1),
    },
    {
      key: 'k',
      description: 'Módulo anterior',
      group: 'En un módulo',
      handler: () => goToModuleDelta(-1),
    },
    {
      key: 'n',
      description: 'Abrir/cerrar el panel de notas',
      group: 'En un módulo',
      handler: () => {
        if (currentModuleId === null) return
        window.dispatchEvent(new CustomEvent('mw:toggle-notes'))
      },
    },
    {
      key: 'i',
      description: 'Modo lectura inmersivo',
      group: 'Vista',
      handler: () => {
        window.dispatchEvent(new CustomEvent('mw:toggle-reading-mode'))
      },
    },
    {
      key: 'f',
      description: 'Modo focus (Pomodoro 25/5)',
      group: 'Vista',
      handler: () => {
        const snap = getFocusSnapshot()
        if (snap.phase === 'idle') startWork()
        else if (snap.running) pause()
        else resume()
      },
    },
    {
      key: 'Escape',
      description: 'Cerrar diálogo o panel lateral',
      group: 'General',
      enableInInputs: true,
      handler: () => {
        if (searchOpen) setSearchOpen(false)
        else if (shortcutsOpen) setShortcutsOpen(false)
        else if (navOpen) setNavOpen(false)
      },
    },
  ]

  useKeyboardShortcuts(shortcuts, [currentModuleId, params.section, navOpen, shortcutsOpen, searchOpen])

  // Shell minimal (sin sidebar y con header simplificado) en cuatro casos:
  //   - alumno sin sesión activa
  //   - estamos en /login
  //   - estamos en /cert/:id (verificación pública, accesible sin auth)
  const { user } = useAuth()
  // Paginas que cualquiera debe poder abrir, con sesion o sin ella
  // y haya terminado el onboarding o no.
  const RUTAS_PUBLICAS = [
    '/login',
    '/privacidad',
    '/verificar',
    '/empresas',
    '/diagnostico',
    '/calculadoras',
  ]
  const isPublicRoute =
    RUTAS_PUBLICAS.some(
      r => location.pathname === r || location.pathname.startsWith(r + '/'),
    ) || location.pathname.startsWith('/cert/')
  const minimal = !user || isPublicRoute

  // Rutas /admin/* usan su propio shell (AdminLayout con header y
  // sidebar admin). No queremos arrastrar el shell del alumno aquí.
  const isAdminRoute = location.pathname.startsWith('/admin')

  // Onboarding del primer acceso: cualquier usuario logueado que no
  // lo haya completado va a /bienvenida antes de entrar a ningún sitio.
  // Excepción: rutas públicas y la propia /bienvenida.
  //
  // Y el panel de administración. La bienvenida pide el nombre para el
  // certificado del curso, que es una cosa de alumno: a quien entra a
  // administrar la plataforma se le estaba pidiendo antes de dejarle
  // pasar, y sin decirle por qué. Esta comprobación iba además antes
  // de decidir la rama de rutas, así que se llevaba por delante el
  // panel entero.
  if (
    user &&
    !user.onboarded &&
    !isPublicRoute &&
    !isAdminRoute &&
    location.pathname !== '/bienvenida' &&
    location.pathname !== '/bienvenida-mw'
  ) {
    return <Navigate to="/bienvenida" replace />
  }

  // El shell del curso (NavSidebar con áreas/módulos, buscador del
  // curso, Pomodoro) solo aplica cuando el alumno está DENTRO de un
  // curso (`/cursos/:slug/*`) Y tiene acceso a ese curso. Un
  // visitante anónimo o un alumno que llega a /cursos/:slug sin
  // acceso ve la landing pública del curso en shell-plataforma; no
  // tiene sentido mostrarle el sidebar de áreas porque no es alumno.
  const courseRouteMatch = location.pathname.match(/^\/cursos\/([^/]+)/)
  const courseRouteSlug = courseRouteMatch?.[1]
  const hasAccessToCourseInUrl =
    !!user && !!courseRouteSlug && (user.assignedCourses?.includes(courseRouteSlug) ?? false)
  const inCourse = !!courseRouteSlug && hasAccessToCourseInUrl
  // El reproductor de leccion trae su propio temario, asi que no
  // debe salir tambien el sidebar de areas del curso.
  // El reproductor trae su propio temario. El sidebar de areas solo
  // tiene sentido en las paginas sueltas que quedan (progreso, repaso,
  // ajustes), no en la leccion.
  const inLessonPlayer = /^\/cursos\/[^/]+\/(leccion\/|modulo\/|[0-9]{2}-)/.test(location.pathname)
  const showCourseShell = !minimal && inCourse && !inLessonPlayer
  // El reproductor de leccion gestiona su propio ancho completo
  const isLessonPlayer = /\/modulo\/\d+\/teoria$/.test(location.pathname)
  if (isAdminRoute) {
    return (
      <RequirePlatformAdmin>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboardPage />} />
              <Route path="empresas" element={<AdminOrganizationsPage />} />
              {/* La vista del alumno es una pantalla mas del panel, no
                  una salida a la plataforma. Ver D-366. */}
              <Route path="rutas" element={<AdminRoutesPage />} />
              <Route path="vista-alumno" element={<AdminStudentPreviewPage />} />
              <Route path="usuarios" element={<AdminUsersListPage />} />
              <Route path="cursos" element={<AdminCoursesPage />} />
              <Route path="cursos/:slug" element={<AdminCourseEditorPage />} />
              <Route path="contenido" element={<AdminBlockEditorPage />} />
              <Route path="compras" element={<AdminPurchasesListPage />} />
              <Route path="analytics" element={<AdminAnalyticsPage />} />
              {/* Catch-all dentro del admin: vuelve al dashboard */}
              <Route path="*" element={<Navigate to="/admin" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </RequirePlatformAdmin>
    )
  }

  // El panel de organizacion vive en /equipo desde D237. El de
  // PV-Learn en /org/:slug/admin se retiro en D265: eran 1216 lineas
  // que no consultaban datos. Se redirige por si alguien guardo el
  // enlace.
  if (/^\/org\/[^/]+\/admin/.test(location.pathname)) {
    return <Navigate to="/equipo" replace />
  }

  return (
    <MaybeCourseProvider slug={courseRouteSlug}>
    <div className="min-h-dvh flex flex-col">
      {/*
        Skip link a11y. Solo visible cuando recibe foco con Tab. Permite a
        usuarios de teclado y lectores de pantalla saltarse el header y la
        sidebar para llegar al contenido principal.
      */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:px-3 focus:py-2 focus:rounded-md focus:bg-neutral-900 focus:text-white focus:text-sm focus:font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-900"
      >
        Saltar al contenido
      </a>

      {/*
        Header global de PV-Learn. NO se renderiza en tenant MentorWallet:
        cada página MW lleva su propio nav consistente con el DS MW
        (paleta C, Bricolage Grotesque, sticky con backdrop blur).
        Renderizarlo aquí duplicaría el branding (logo PV encima de logo
        MW) y rompería el look.
      */}
      <MwNavBar />
      <div className="flex flex-1 min-h-0">
        {showCourseShell && <NavSidebar open={navOpen} onClose={() => setNavOpen(false)} />}
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 min-w-0 focus:outline-none"
        >
          <div style={showCourseShell ? { padding: isLessonPlayer ? 0 : '24px 24px 48px' } : { paddingBottom: 48 }}>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Rutas públicas (no requieren sesión) */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/cert/:verificationId" element={<VerifyPage />} />

              {/* Diagnóstico (público, anónimo) — el motor del producto MW. */}
              <Route path="/diagnostico" element={<DiagnosticPage />} />
              <Route path="/diagnostico/r/:profileSlug" element={<DiagnosticResultPage />} />
              <Route path="/empresas" element={<B2BLandingPage />} />
              <Route path="/equipo" element={<TeamPanelPage />} />
              <Route path="/verificar" element={<VerifyCertificatePage />} />
              {/* El codigo tambien en la ruta: es como lo escribe
                  quien lo ha recibido dictado o impreso. */}
              <Route path="/verificar/:codigo" element={<VerifyCertificatePage />} />
              <Route path="/mi-ruta" element={<MyPathPage />} />

              {/* `/` es el catálogo de cursos asignados al alumno. Si solo
                  hay uno, CatalogPage redirige a su home. */}
              {/* Home pública.
                  - Visitante anónimo → MentorWalletLandingPage.
                  - Usuario logueado → CatalogPage (hero + Mis cursos
                    + Explorar + Ayuda). */}
              <Route path="/" element={<HomeSwitcher />} />
              {/* El panel tiene su propia ruta, para poder enlazarlo */}
              <Route path="/panel" element={<PanelAlumno />} />

              {/* Política de privacidad — pública, accesible desde
                  footer y banner de cookies. */}
              <Route path="/privacidad" element={<PrivacyPage />} />

              {/* Perfil global del usuario: datos de cuenta, sesión.
                  No depende de ningún curso. */}
              <Route
                path="/perfil"
                element={<RequireAuth><ProfilePage /></RequireAuth>}
              />
              <Route
                path="/cuenta/suscripcion"
                element={<RequireAuth><SubscriptionPage /></RequireAuth>}
              />
              <Route
                path="/cuenta/privacidad"
                element={<RequireAuth><PrivacySettingsPage /></RequireAuth>}
              />

              {/* Onboarding del primer acceso (nombre real). El gate de
                  arriba redirige aquí; la página se autoguarda si el
                  usuario ya está onboarded o no tiene sesión. */}
              <Route
                path="/bienvenida"
                element={<RequireAuth><OnboardingPage /></RequireAuth>}
              />
              <Route
                path="/bienvenida-mw"
                element={<RequireAuth><MwOnboardingPage /></RequireAuth>}
              />

              {/* Checkout B2C: el flujo de compra del curso. Las
                  páginas no requieren login (se puede comprar y luego
                  hacer signup con el mismo email), pero si el usuario
                  está logueado vinculamos la compra a su user_id
                  desde el primer momento. */}
              <Route path="/comprar" element={<SoloSiSeVendeDirecto><ProductCatalogPage /></SoloSiSeVendeDirecto>} />
              <Route path="/comprar/mentor-wallet" element={<SoloSiSeVendeDirecto><PaywallPage /></SoloSiSeVendeDirecto>} />
              <Route path="/comprar/regalo" element={<SoloSiSeVendeDirecto><GiftPurchasePage /></SoloSiSeVendeDirecto>} />
              <Route path="/comprar/regalo/exito" element={<GiftSuccessPage />} />
              <Route path="/comprar/regalo-completo" element={<SoloSiSeVendeDirecto><GiftBundlePurchasePage /></SoloSiSeVendeDirecto>} />
              <Route path="/comprar/regalo-completo/exito" element={<GiftSuccessPage />} />
              <Route path="/comprar/bundle" element={<SoloSiSeVendeDirecto><BundlePurchasePage /></SoloSiSeVendeDirecto>} />
              <Route path="/comprar/bundle/exito" element={<BundleSuccessPage />} />
              <Route path="/canjear" element={<GiftRedeemPage />} />
              <Route path="/canjear/:code" element={<GiftRedeemPage />} />
              <Route path="/calculadoras" element={<CalculatorsCatalogPage />} />
              <Route path="/calculadoras/:slug" element={<CalculatorPage />} />
              <Route path="/sesion-1to1" element={<SoloSiSeVendeDirecto><SessionOneToOnePage /></SoloSiSeVendeDirecto>} />
              {/* Comodin de compra por curso. Llevaba sin guarda: con
                  el modo en b2b, /comprar/mw-hipoteca seguia pintando
                  el formulario de pago aunque el endpoint lo rechazara
                  despues. Ahora se corta antes. */}
              <Route path="/comprar/:slug" element={<SoloSiSeVendeDirecto><CheckoutPage /></SoloSiSeVendeDirecto>} />
              <Route path="/comprar/:slug/exito" element={<CheckoutSuccessPage />} />
              <Route path="/comprar/:slug/cancelado" element={<CheckoutCancelledPage />} />

              {/* /ajustes legacy: ya no asume un curso por defecto.
                  Los ajustes son por curso (modo acceso, borrar
                  progreso, etc.) y mezclar plataforma con curso fue
                  parte de la deuda heredada de cuando solo existía
                  Agent 365. Ahora el alumno vuelve al catálogo y
                  elige desde qué curso quiere ajustar. */}
              <Route
                path="/ajustes"
                element={<Navigate to="/" replace />}
              />

              {/* Rutas del curso.
                  - `/cursos/:slug/` (sin sub-path): pública por
                    diseño. CourseRootSwitcher decide qué renderizar
                    según el usuario:
                      · Anónimo o sin acceso → landing pública.
                      · Con acceso → dashboard del curso (HomePage).
                  - Sub-rutas (modulo, examen, certificado, ajustes,
                    progreso, repaso): solo accesibles con auth Y
                    acceso al curso. RequireCourseAccess se encarga;
                    si falta acceso, redirige a la landing pública. */}
              <Route path="/cursos/:slug">
                <Route index element={<CourseRootSwitcher />} />
                <Route
                  path="*"
                  element={
                    <RequireCourseAccess>
                      <Routes>
                        {/* El alumno tiene un solo camino: el
                            reproductor de leccion. Todo lo demas
                            (teoria, ejercicio, recursos, apuntes)
                            son pestanas dentro de el. */}
                        <Route path="leccion/:moduleId" element={<LessonPage />} />
                        <Route path="progreso" element={<ProgressPage />} />
                        <Route path="apuntes" element={<NotesPage />} />
                        <Route path="repaso" element={<RepasoPage />} />
                        <Route path="ajustes" element={<SettingsPage />} />
                        <Route path="certificado" element={<CertificatePage />} />

                        {/* Rutas antiguas: se redirigen para no romper
                            enlaces guardados ni el historial. */}
                        <Route path="modulo/:id" element={<ModuleToLessonRedirect />} />
                        <Route path="modulo/:id/:section" element={<ModuleToLessonRedirect />} />

                        {/* Por slug del modulo: /cursos/x/02-fondo... */}
                        <Route path=":moduleSlug" element={<LessonPage />} />
                        <Route path="*" element={<CourseRootSwitcher />} />
                      </Routes>
                    </RequireCourseAccess>
                  }
                />
              </Route>

              {/* Aliases legacy: bookmarks anteriores a Fase 8 siguen
                  vivos. Las rutas genéricas (sin slug ni ID
                  contextual) ahora van al catálogo en lugar de
                  saltar a un curso por defecto; eso era parte del
                  acoplamiento heredado. Las rutas con ID
                  (módulo/certificado) sí mantienen el redirect al
                  único curso histórico porque esos IDs solo existen
                  en agent365-cert. */}
              <Route path="/progreso" element={<Navigate to="/" replace />} />
              <Route path="/examen" element={<Navigate to="/" replace />} />
              <Route path="/repaso" element={<Navigate to="/" replace />} />
              <Route path="/modulo/:id" element={<LegacyModuleRedirect />} />
              <Route path="/modulo/:id/:section" element={<LegacyModuleRedirect />} />
              <Route
                path="/certificado/:attemptId"
                element={<LegacyCertificateRedirect />}
              />
              <Route path="*" element={<MwNotFoundPage />} />
            </Routes>
          </Suspense>
          </div>
        </main>
      </div>

      <ShortcutsModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        shortcuts={shortcuts.filter(s => s.key !== 'Escape')}
      />

      {showCourseShell && searchOpen && (
        <Suspense fallback={null}>
          <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
        </Suspense>
      )}

      {/* Tarjeta flotante del Pomodoro. Solo en shell de curso porque
          es una herramienta para sesiones de estudio dentro del curso. */}
      {showCourseShell && <FocusTimer />}
    </div>
    </MaybeCourseProvider>
  )
}

/**
 * Envoltorio condicional del CourseProvider:
 *   - Si la URL está en `/cursos/:slug/*` (tenemos un slug real),
 *     envuelve los children con el CourseProvider apuntando a ese
 *     slug — los componentes pueden leer el curso con useCourse().
 *   - Si la URL es de plataforma (`/`, `/perfil`, `/comprar/*`, …),
 *     NO se monta el provider — `useCourseOptional()` devuelve null
 *     y `useCourse()` lanza si se llama desde fuera de la rama de
 *     curso (comportamiento correcto: ningún curso activo).
 *
 * Antes, AppShell siempre envolvía con un slug fallback al curso por
 * defecto. Eso provocaba que Header.tsx pensara que estaba "en un
 * curso" en /perfil o /comprar y mostrara la mini-tarjeta del curso
 * en sitios donde no tocaba.
 */
function MaybeCourseProvider({
  slug,
  children,
}: {
  slug: string | undefined
  children: React.ReactNode
}) {
  if (!slug) return <>{children}</>
  return <CourseProvider slug={slug}>{children}</CourseProvider>
}

/* --------------------- Gate de autenticación --------------------- */

/**
 * Wrapper que exige sesión activa para renderizar `children`. Si no
 * hay usuario, redirige a `/login` y pasa la ruta original en
 * `state.from` para volver tras el sign-in.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth()
  const location = useLocation()
  // Esperamos a resolver la sesión antes de decidir; ver
  // RequirePlatformAdmin para los detalles del race condition.
  if (status === 'loading') {
    return <PageFallback />
  }
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    )
  }
  return <>{children}</>
}

/**
 * Decide qué renderizar en `/cursos/:slug/` según el contexto:
 * - Anónimo o usuario sin acceso al curso → landing pública.
 * - Usuario con acceso → dashboard interno del curso (HomePage).
 *
 * Es el punto de entrada "soft" al curso: cualquier visitante puede
 * llegar a esta URL, ver de qué va el curso y decidir si compra. Si
 * ya tiene acceso, salta directo al contenido. Mismo patrón que
 * Udemy o Coursera.
 */
/**
 * Switcher de la home `/`. Decide qué renderizar según la sesión y
 * el tenant:
 *   - Anónimo en tenant 'mentor-wallet' → MentorWalletLandingPage
 *     (landing dedicada con diagnóstico CTA).
 *   - Anónimo en cualquier otro tenant → landing de MW
 *     (marketing público genérico, catálogo de cursos).
 *   - Autenticado → CatalogPage (hero personal + Mis cursos + …),
 *     independientemente del tenant.
 *
 * El usuario autenticado siempre va al catalog (la experiencia del
 * curso es la misma sea el tenant que sea); solo la landing pública
 * cambia por tenant.
 *
 * Igual que CourseRootSwitcher pero para la raíz. Mantiene una sola
 * URL semántica ('/' = "página de inicio") sin redirects raros.
 */
/**
 * El panel del alumno, en su propia ruta.
 *
 * La raiz sigue repartiendo segun quien entre, pero desde aqui el
 * panel se puede enlazar, marcar y compartir, que con la raiz no se
 * podia porque para un visitante es la portada.
 */
function PanelAlumno() {
  const { user, status } = useAuth()

  if (status === 'loading') return <PageFallback />
  if (!user) return <Navigate to="/login" state={{ from: '/panel' }} replace />

  return <MwDashboardPage />
}

function HomeSwitcher() {
  const { user, status } = useAuth()
  const { isLoading: cargandoRol } = useIsAdmin()

  const { data: rol, isLoading: cargandoOrg } = useQuery({
    queryKey: ['my-role', user?.id],
    queryFn: fetchMyRole,
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  })
  const tenantSlug = resolveTenantSlug()

  if (status === 'loading' || (user && (cargandoRol || cargandoOrg))) {
    return <PageFallback />
  }

  // Quien gestiona el programa de su empresa y no tiene licencia
  // propia va al seguimiento: el panel del alumno le diria que no
  // tiene acceso al curso, que es cierto y no viene al caso.
  if (user && rol?.isManager && !rol.hasLicence) {
    return <Navigate to="/equipo" replace />
  }

  // Con sesion, otros tenants siguen yendo a su catalogo generico.
  if (user && tenantSlug !== 'mentor-wallet') {
    return <CatalogPage />
  }

  // La raiz de MentorWallet es la portada publica, tambien con la
  // sesion abierta. Antes redirigia a /panel, y eso dejaba el sitio
  // comercial inalcanzable para quien ya habia entrado alguna vez:
  // escribir el dominio a secas te metia en tu sesion en vez de
  // ensenarte la web, que es justo lo contrario de lo que espera
  // cualquiera. Al panel se llega por el logo, por "Mis cursos" o
  // por /panel directamente, que estan siempre a la vista.
  return <MentorWalletLandingPage />
}

/**
 * Manda las rutas antiguas al reproductor.
 *
 * /modulo/2/teoria y /modulo/2/quiz-practica se convierten en
 * /leccion/2, porque ahora las secciones son pestanas dentro de la
 * leccion. Se conservan para no romper enlaces guardados.
 */
function ModuleToLessonRedirect() {
  const { id } = useParams<{ id: string }>()
  const { href } = useCourse()
  return <Navigate to={href(`leccion/${id ?? 1}`)} replace />
}

/**
 * Cierra las paginas de compra cuando solo se vende a empresas.
 *
 * En vez de un 404, lleva a /empresas, que es la via valida en ese
 * modo. Un enlace guardado o compartido sigue llevando a algun
 * sitio con sentido.
 */
function SoloSiSeVendeDirecto({ children }: { children: React.ReactNode }) {
  const { canBuyDirect, isLoading } = usePlatformSettings()
  if (isLoading) return <PageFallback />
  if (!canBuyDirect) return <Navigate to="/empresas" replace />
  return <>{children}</>
}

function CourseRootSwitcher() {
  const { user, status } = useAuth()
  const { slug } = useParams<{ slug: string }>()
  // Mientras la sesión carga, evita parpadeos (anónimo → con sesión).
  if (status === 'loading') {
    return <PageFallback />
  }
  if (user && slug && user.assignedCourses?.includes(slug)) {
    return <HomePage />
  }
  return <CoursePublicLandingPage />
}

/**
 * Wrapper para sub-rutas de un curso (modulo, examen, ajustes…).
 * Exige sesión Y acceso al curso. Si falta sesión → login. Si tiene
 * sesión pero no tiene el curso asignado → landing pública del
 * curso (no a `/`) para que pueda comprarlo desde ahí.
 */
function RequireCourseAccess({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth()
  const { slug } = useParams<{ slug: string }>()
  const location = useLocation()
  const subStatus = useSubscriptionStatus()

  // El precio dice si el curso es gratuito
  const { data: pricing, isLoading: cargandoPrecio } = useQuery({
    queryKey: ['course-pricing', slug],
    queryFn: () => getCoursePricing(slug!),
    enabled: Boolean(slug),
    staleTime: 10 * 60 * 1000,
  })

  if (status === 'loading' || cargandoPrecio) {
    return <PageFallback />
  }
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    )
  }

  // Un curso es gratuito si su precio lo dice, no por estar en una
  // lista escrita a mano: al anadir otro habria que acordarse de
  // tocar el codigo, y nadie se acuerda.
  const isFree = pricing?.b2cPriceCents === 0

  // Cualquier curso de pago, no solo el principal. Antes los
  // monograficos quedaban abiertos a quien tuviera el enlace.
  if (!isFree && !subStatus.hasAccess && subStatus.status !== 'loading') {
    return <MwSubscriptionGate free={false}><></></MwSubscriptionGate>
  }

  return <>{children}</>
}

/**
 * Wrapper para rutas /admin/*. Exige sesión + flag platform_admin.
 * Si hay sesión pero no es admin, redirige a la home (no a /login,
 * porque la sesión es válida, simplemente no tiene permisos).
 */
function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  const { user, status } = useAuth()
  // Mientras el AuthContext carga la sesión (status='loading'), `user` es
  // null. No podemos decidir si redirigir a /login porque aún no sabemos
  // si hay sesión. Mostrar un fallback minimal (o nada) hasta resolver.
  if (status === 'loading') {
    return <PageFallback />
  }
  if (!user) {
    // Se pinta el acceso aqui mismo, sin cambiar la URL. Antes
    // rebotaba a /login y la barra dejaba de poner /admin, asi que
    // quien queria entrar a administrar acababa en la pantalla del
    // alumno sin saber muy bien por que. LoginPage reconoce que el
    // destino empieza por /admin y se adapta.
    return <LoginPage />
  }
  if (!user.isPlatformAdmin) {
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

/* --------------------- Aliases / redirects --------------------- */

function LegacyModuleRedirect() {
  const slug = defaultCourseSlug()
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/cursos/${slug}/leccion/${id}`} replace />
}

function LegacyCertificateRedirect() {
  const slug = defaultCourseSlug()
  const { attemptId } = useParams<{ attemptId: string }>()
  return <Navigate to={`/cursos/${slug}/certificado/${attemptId}`} replace />
}

// Cliente de React Query a nivel de módulo (estable entre renders). Defaults
// conservadores para un panel: no refetch al enfocar la ventana, 30s de
// staleTime y un único reintento.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
})

export function App() {
  const [navOpen, setNavOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={basename}>
        <TenantProvider>
          <AuthProvider>
            <ToastProvider>
              <AppShell
                navOpen={navOpen}
                setNavOpen={setNavOpen}
                shortcutsOpen={shortcutsOpen}
                setShortcutsOpen={setShortcutsOpen}
                searchOpen={searchOpen}
                setSearchOpen={setSearchOpen}
              />
              {/* Banner que avisa al usuario cuando hay un deploy nuevo
                  disponible. Se sirve lazy para no cargar el cliente del
                  SW si la app se ejecuta sin PWA (build offline). */}
              <Suspense fallback={null}>
              </Suspense>

              {/* Información de cookies (RGPD) — solo cookies técnicas
                  en uso, por lo que es informativo, no consent gate. */}
              <Suspense fallback={null}>
                <CookieBanner />
              </Suspense>
            </ToastProvider>
          </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
